import type { strokeDashTypes } from 'react-financial-charts';
import { GenericChartComponent, getMouseCanvas, getStrokeDasharrayCanvas, noop } from 'react-financial-charts';
import * as React from 'react';
import type { MoreProps, MorePropsChartConfig } from '@/types/commontypes';
import type { ScaleContinuousNumeric } from 'd3-scale';
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const ChartComponent = GenericChartComponent as unknown as React.ComponentType<Record<string, unknown>>;

export interface StraightLineProps {
  readonly x1Value?: number;
  readonly x2Value?: number;
  readonly y1Value?: number;
  readonly y2Value?: number;
  readonly x3Value?: number;
  readonly y3Value?: number;
  readonly interactiveCursorClass?: string;
  readonly strokeStyle: string;
  readonly strokeWidth?: number;
  readonly strokeDasharray?: strokeDashTypes;
  readonly type:
    | 'XLINE' // extends from -Infinity to +Infinity
    | 'RAY' // extends to +/-Infinity in one direction
    | 'LINE'; // extends between the set bounds
  readonly onEdge1Drag?: () => void; // func
  readonly onEdge2Drag?: () => void; // func
  readonly onDragStart?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDrag?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDragComplete?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onUnHover?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly defaultClassName?: string;
  readonly r?: number;
  readonly edgeFill?: string;
  readonly edgeStroke?: string;
  readonly edgeStrokeWidth?: number;
  readonly withEdge?: boolean;
  readonly tolerance?: number;
  readonly selected?: boolean;
  readonly extendLeft?: boolean;
  readonly extendRight?: boolean;
  readonly onClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onMouseDown?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly onDoubleClick?: (e: React.MouseEvent, moreProps: MoreProps) => void;
  readonly fillStyle?: string;
}

export class InteractiveStraightLine extends React.Component<StraightLineProps> {
  public static defaultProps = {
    onEdge1Drag: noop,
    onEdge2Drag: noop,
    edgeStrokeWidth: 2,
    edgeStroke: '#1e53e6',
    edgeFill: '#FFFFFF',
    r: 5,
    withEdge: false,
    strokeWidth: 1,
    strokeDasharray: 'Solid',
    children: noop,
    tolerance: 7,
    selected: false,
    extendLeft: false,
    extendRight: false,
  };

  public render() {
    const { selected, interactiveCursorClass } = this.props;
    const { onDragStart, onDrag, onDragComplete, onHover, onUnHover, onDoubleClick: _onDoubleClick } = this.props;
    return (
      <ChartComponent
        isHover={this.isHover}
        canvasToDraw={getMouseCanvas}
        canvasDraw={this.drawOnCanvas}
        interactiveCursorClass={interactiveCursorClass}
        selected={selected}
        onDragStart={onDragStart}
        onDrag={onDrag}
        onDragComplete={onDragComplete}
        onHover={onHover}
        onUnHover={onUnHover}
        drawOn={['mousemove', 'pan', 'drag']}
        onClick={this.handleClick}
        onMouseDown={this.handleMouseDown}
        onDoubleClick={this.handleDoubleClick}
        // key={interactiveCursorClass}
      />
    );
  }

  private readonly isHover = (moreProps: MoreProps) => {
    if (!moreProps) return null;
    const { tolerance, onHover } = this.props;

    if (onHover !== undefined) {
      const { x1Value, x2Value, y1Value, y2Value, type } = this.props;
      const { mouseXY, xScale } = moreProps;
      const {
        chartConfig: { yScale, height },
      } = moreProps;

      // 마우스가 차트 섹션 밖이면 호버 불가 (서브차트 간 이동 시 호버 고착 방지)
      if (mouseXY[1] < 0 || mouseXY[1] > height) return false;

      const hovering = isHovering({
        x1Value,
        y1Value,
        x2Value,
        y2Value,
        mouseXY,
        type,
        tolerance,
        xScale,
        yScale,
      });
      return hovering;
    }
    return false;
  };

  private readonly drawOnCanvas = (ctx: CanvasRenderingContext2D, moreProps: MoreProps) => {
    const {
      strokeWidth = InteractiveStraightLine.defaultProps.strokeWidth,
      strokeDasharray,
      strokeStyle,
      x1Value,
      x2Value,
    } = this.props;

    const {
      chartConfig: { width },
    } = moreProps;

    ctx.lineWidth = strokeWidth;
    ctx.strokeStyle = strokeStyle;

    const lineDash = getStrokeDasharrayCanvas(strokeDasharray);

    ctx.setLineDash(lineDash);

    ctx.beginPath();
    // Check if x1Value and x2Value are defined
    const hasX = x1Value !== undefined && x2Value !== undefined;
    const points = helper(this.props, moreProps);
    if (!points) return;
    const { x1, y1, x2, y2 } = points;
    if (hasX) {
      // Use scaled x1, x2 from helper
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
    } else {
      // Use fixed x position
      const x = width - 350;
      ctx.moveTo(x, y1);
      ctx.lineTo(x, y2);
    }
    ctx.stroke();
  };

  private readonly handleClick = (e: React.MouseEvent, moreProps: MoreProps) => {
    const isHovered = this.isHover(moreProps);
    if (isHovered && this.props.onClick) {
      this.props.onClick(e, moreProps);
    }
  };

  private readonly handleMouseDown = (e: React.MouseEvent, moreProps: MoreProps) => {
    const isHovered = this.isHover(moreProps);
    if (isHovered && this.props.onMouseDown) {
      this.props.onMouseDown(e, moreProps);
    }
  };

  private readonly handleDoubleClick = (e: React.MouseEvent, moreProps: MoreProps) => {
    if (this.isHover(moreProps)) {
      const { onDoubleClick } = this.props;
      if (onDoubleClick) {
        onDoubleClick(e, moreProps);
      }
    }
  };
}

export function isHovering2(start: number[], end: number[], [mouseX, mouseY]: number[], tolerance: number) {
  const m = getSlope(start, end);

  if (m !== undefined) {
    const b = getYIntercept(m, end);
    const y = m * mouseX + b;
    return (
      mouseY < y + tolerance &&
      mouseY > y - tolerance &&
      mouseX > Math.min(start[0], end[0]) - tolerance &&
      mouseX < Math.max(start[0], end[0]) + tolerance
    );
  } else {
    return (
      mouseY >= Math.min(start[1], end[1]) &&
      mouseY <= Math.max(start[1], end[1]) &&
      mouseX < start[0] + tolerance &&
      mouseX > start[0] - tolerance
    );
  }
}

interface IsHoveringParams {
  x1Value?: number;
  y1Value?: number;
  x2Value?: number;
  y2Value?: number;
  mouseXY: [number, number];
  type: StraightLineProps['type'];
  tolerance: number | undefined;
  xScale: ScaleContinuousNumeric<number, number>;
  yScale: ScaleContinuousNumeric<number, number>;
}

export function isHovering({
  x1Value,
  y1Value,
  x2Value,
  y2Value,
  mouseXY,
  type,
  tolerance,
  xScale,
  yScale,
}: IsHoveringParams) {
  if (x1Value == null || x2Value == null || y1Value == null || y2Value == null) return false;
  const line = generateLine({
    type,
    start: [x1Value, y1Value],
    end: [x2Value, y2Value],
    xScale,
    yScale,
  });

  const start = [xScale(line.x1), yScale(line.y1)];
  const end = [xScale(line.x2), yScale(line.y2)];

  const m = getSlope(start, end);
  const [mouseX, mouseY] = mouseXY;

  if (m !== undefined) {
    const b = getYIntercept(m, end);
    const y = m * mouseX + b;

    const result =
      mouseY < y + tolerance! &&
      mouseY > y - tolerance! &&
      mouseX > Math.min(start[0], end[0]) - tolerance! &&
      mouseX < Math.max(start[0], end[0]) + tolerance!;

    return result;
  } else {
    return (
      mouseY >= Math.min(start[1], end[1]) &&
      mouseY <= Math.max(start[1], end[1]) &&
      mouseX < start[0] + tolerance! &&
      mouseX > start[0] - tolerance!
    );
  }
}

interface HelperProps {
  x1Value?: number;
  x2Value?: number;
  y1Value?: number;
  y2Value?: number;
  type: StraightLineProps['type'];
  extendLeft?: boolean;
  extendRight?: boolean;
}

export function helper(props: HelperProps, moreProps: MoreProps) {
  const { x1Value, x2Value, y1Value, y2Value, type, extendLeft, extendRight } = props;
  const mp = moreProps;
  const {
    xScale,
    chartConfig: { yScale },
  } = mp;
  if (x1Value == null || x2Value == null || y1Value == null || y2Value == null) return undefined;
  const modLine = generateLine({
    type,
    start: [x1Value, y1Value],
    end: [x2Value, y2Value],
    xScale,
    yScale,
    extendLeft,
    extendRight,
  });

  const x1 = xScale(modLine.x1);
  const y1 = yScale(modLine.y1);
  const x2 = xScale(modLine.x2);
  const y2 = yScale(modLine.y2);

  return {
    x1,
    y1,
    x2,
    y2,
  };
}

export function getSlope(start: number[], end: number[]) {
  const m /* slope */ = end[0] === start[0] ? undefined : (end[1] - start[1]) / (end[0] - start[0]);
  return m;
}
export function getYIntercept(m: number, end: number[]) {
  const b /* y intercept */ = -1 * m * end[0] + end[1];
  return b;
}

interface GenerateLineParams {
  type: StraightLineProps['type'];
  start: number[];
  end: number[];
  xScale: MoreProps['xScale'];
  yScale: MorePropsChartConfig['yScale'];
  extendLeft?: boolean;
  extendRight?: boolean;
}

interface CoordinateResult {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export function generateLine({ type, start, end, xScale, yScale, extendLeft, extendRight }: GenerateLineParams) {
  const m /* slope */ = getSlope(start, end);
  const b /* y intercept */ = m !== undefined ? getYIntercept(m, start) : 0;

  switch (type) {
    case 'XLINE':
      return getXLineCoordinates({
        start,
        end,
        xScale,
        yScale,
        m,
        b,
      });
    case 'RAY':
      return getRayCoordinates({
        start,
        end,
        xScale,
        yScale,
        m,
        b,
      });
    default:
    case 'LINE':
      return getLineCoordinates({
        start,
        end,
        extendLeft,
        extendRight,
        xScale,
        yScale,
        m,
        b,
      });
  }
}

interface CoordFnParams {
  start: number[];
  end: number[];
  xScale: MoreProps['xScale'];
  yScale: MorePropsChartConfig['yScale'];
  m: number | undefined;
  b: number;
  extendLeft?: boolean;
  extendRight?: boolean;
}

function getXLineCoordinates({ start, end, xScale, yScale, m, b }: CoordFnParams): CoordinateResult {
  const [xBegin, xFinish] = xScale.domain();
  const [yBegin, yFinish] = yScale.domain();

  if (end[0] === start[0]) {
    return {
      x1: end[0],
      y1: yBegin,
      x2: end[0],
      y2: yFinish,
    };
  }
  if (m === undefined) return { x1: start[0], y1: start[1], x2: end[0], y2: end[1] };
  const [x1, x2] = end[0] > start[0] ? [xBegin, xFinish] : [xFinish, xBegin];

  return {
    x1,
    y1: m * x1 + b,
    x2,
    y2: m * x2 + b,
  };
}

function getRayCoordinates({ start, end, xScale, yScale, m, b }: CoordFnParams): CoordinateResult {
  const [xBegin, xFinish] = xScale.domain();
  const [yBegin, yFinish] = yScale.domain();

  const x1 = start[0];
  if (end[0] === start[0]) {
    return {
      x1,
      y1: start[1],
      x2: x1,
      y2: end[1] > start[1] ? yFinish : yBegin,
    };
  }
  if (m === undefined) return { x1, y1: start[1], x2: end[0], y2: end[1] };

  const x2 = end[0] > start[0] ? xFinish : xBegin;

  return {
    x1,
    y1: m * x1 + b,
    x2,
    y2: m * x2 + b,
  };
}

function getLineCoordinates({
  start,
  end,
  extendLeft,
  extendRight,
  xScale,
  yScale,
  m,
  b,
}: CoordFnParams): CoordinateResult {
  let [x1, y1] = start;
  let [x2, y2] = end;
  const [xBegin, xFinish] = xScale.domain();
  const [yBegin, yFinish] = yScale.domain();

  // Vertical line (m === undefined): extend direction is vertical, no slope calculation needed
  if (x1 === x2) {
    if (extendLeft && extendRight) {
      y1 = yBegin;
      y2 = yFinish;
    } else if (extendLeft) {
      y1 = y2 > y1 ? yBegin : yFinish;
    } else if (extendRight) {
      y2 = y2 > y1 ? yFinish : yBegin;
    }
    return { x1, y1, x2: x1, y2 };
  }

  // Non-vertical line: m is guaranteed to be defined here (start[0] !== end[0])
  if (m === undefined) return { x1, y1, x2, y2 };

  if (extendLeft) {
    x1 = end[0] > start[0] ? xBegin : xFinish;
    y1 = m * x1 + b;
    y2 = m * x2 + b;
  }

  if (extendRight) {
    y1 = m * x1 + b;
    x2 = end[0] > start[0] ? xFinish : xBegin;
    y2 = m * x2 + b;
  }

  if (extendLeft && extendRight) {
    [x1, x2] = end[0] > start[0] ? [xBegin, xFinish] : [xFinish, xBegin];
    y1 = m * x1 + b;
    y2 = m * x2 + b;
  }

  return { x1, y1, x2, y2 };
}
